import Dynamicore, { SERVICES } from '../index';

const MAX_RETRIES = 10;
const USER_INFO_CACHE_TTL_MS = 1200;
const userInfoCache = new Map();
const inFlightUserInfoRequests = new Map();

const buildCacheKey = (args = []) => {
  const sortedArgs = [...new Set(args)].sort();
  return sortedArgs.length ? sortedArgs.join('|') : 'base';
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const getCachedUserInfo = (key) => {
  const cached = userInfoCache.get(key);
  if (!cached) return null;

  if (cached.expiresAt < Date.now()) {
    userInfoCache.delete(key);
    return null;
  }

  return clone(cached.value);
};

const setCachedUserInfo = (key, value) => {
  userInfoCache.set(key, {
    expiresAt: Date.now() + USER_INFO_CACHE_TTL_MS,
    value,
  });
};

export const clearUserInfoCache = () => {
  userInfoCache.clear();
  inFlightUserInfoRequests.clear();
};

async function fetchUserInfo(args = []) {
  let retries = 0;
  let user = null;
  const completedRequests = {};

  while (retries < MAX_RETRIES) {
    try {
      if (!user) {
        const { values: info } = await Dynamicore(SERVICES.USERS_GET_INFO).get();
        const key = `dcore:${info?.company}:client:`;
        const peopleId = String((info?.json_rol?.Statement ?? [])
          .find((item) => item.Resource.startsWith(key))?.Resource ?? '')
          .replace(key, '');

        user = {
          id: info.user,
          data: args.length > 0 ? {} : undefined,
          entities: {
            accountIds: [],
            companyId: info.company,
            groupId: info.group,
            peopleId: parseInt(peopleId, 10),
          },
        };
      }

      if (args.includes('company') && !completedRequests.company) {
        user.data.company = await Dynamicore(SERVICES.COMPANY).get({
          id: user?.entities?.companyId,
        });
        completedRequests.company = true;
      }

      if (args.includes('people') && !completedRequests.people) {
        user.data.people = await Dynamicore(SERVICES.PEOPLE).get({
          id: user?.entities?.peopleId,
        });

        user.data.peopleType = await Dynamicore(SERVICES.PEOPLE_TYPES).get({
          id: user?.data?.people?.client_type ?? 0,
        });

        user.entities.peopleTypeId = user?.data?.peopleType?.id;
        completedRequests.people = true;
      }

      if (args.includes('relationships') && !completedRequests.relationships) {
        const { values: relationships = [] } = await Dynamicore(SERVICES.PEOPLE_ORGANIZATIONAL_OLD).get({
          borrower: user?.entities?.peopleId,
        });

        const { values: relatedPeople = [] } = await Dynamicore(SERVICES.PEOPLE_ORGANIZATIONAL).get({
          borrower: user?.entities?.peopleId,
          limit: 1000,
          page: 1,
        });

        relationships.forEach((item) => {
          item.people = relatedPeople.find((relatedItem) => relatedItem.id === item.person);
        });

        user.data.relationships = relationships;
        completedRequests.relationships = true;
      }

      return user;
    } catch (error) {
      retries++;

      if (retries === MAX_RETRIES) {
        throw new Error('No se pudo obtener toda la información del usuario después de varios intentos.');
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }
}

async function getUserInfo(...args) {
  const cacheKey = buildCacheKey(args);
  const cachedValue = getCachedUserInfo(cacheKey);
  if (cachedValue) {
    return cachedValue;
  }

  const inFlightRequest = inFlightUserInfoRequests.get(cacheKey);
  if (inFlightRequest) {
    return clone(await inFlightRequest);
  }

  const requestPromise = fetchUserInfo(args)
    .then((user) => {
      setCachedUserInfo(cacheKey, user);
      return user;
    })
    .finally(() => {
      inFlightUserInfoRequests.delete(cacheKey);
    });

  inFlightUserInfoRequests.set(cacheKey, requestPromise);

  const user = await requestPromise;
  return clone(user);
}

export default getUserInfo;
